import { z } from 'zod';

/**
 * Recipients — the people, bound to the signing roles the canvas already uses.
 *
 * Roles are defined during authoring ("Landlord", "Contractor"); recipients are
 * attached to those roles at send time. Templates store roles and never people,
 * so this split is what lets an envelope become a template unchanged.
 */

export const RECIPIENT_ROLES = [
  'signer', 'in_person_signer', 'approver', 'cc', 'certified_delivery',
] as const;
export type RecipientRole = (typeof RECIPIENT_ROLES)[number];

export const RECIPIENT_ROLE_INFO: Record<RecipientRole, { label: string; hint: string; signs: boolean }> = {
  signer: {
    label: 'Signer', signs: true,
    hint: 'Completes their assigned fields and signs.',
  },
  in_person_signer: {
    label: 'In-person signer', signs: true,
    hint: 'Signs on your device while you host. The audit trail records the network details as YOURS, not theirs, and says so — see compliance-research.md § 5.3.',
  },
  approver: {
    label: 'Approver', signs: false,
    hint: 'Approves or declines. Applies no signature.',
  },
  cc: {
    label: 'Receives a copy', signs: false,
    hint: 'Gets the completed documents. Signs nothing, and is not sent a signing link.',
  },
  certified_delivery: {
    label: 'Certified delivery', signs: false,
    hint: 'Must open and acknowledge receipt. Signs nothing.',
  },
};

/** How the recipient proves they are who the invitation was addressed to. */
export const AUTH_METHODS = ['email_only', 'access_code'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

/**
 * Channels an access code may travel by.
 *
 * There is deliberately NO email option. A code sent to the same mailbox as the
 * link proves nothing — whoever can read the mailbox has both halves — and would
 * add friction while recording an out-of-band claim that is false. The invitation
 * template has no binding for the code either, so this is enforced by absence
 * rather than by a warning. requirements.md § 1.10.
 */
export const CODE_CHANNELS = ['phone', 'sms', 'in_person', 'other'] as const;
export type CodeChannel = (typeof CODE_CHANNELS)[number];

export const CODE_CHANNEL_LABELS: Record<CodeChannel, string> = {
  phone: 'Phone call', sms: 'Text message', in_person: 'In person', other: 'Other out-of-band',
};

export const RECIPIENT_STATUSES = [
  'pending', 'notified', 'delivered', 'opened', 'consented',
  'in_progress', 'completed', 'declined', 'bounced',
] as const;
export type RecipientStatus = (typeof RECIPIENT_STATUSES)[number];

export interface Recipient {
  id: string;
  roleId: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  recipientRole: RecipientRole;
  /** Equal values sign in parallel; lower values go first. */
  routingOrder: number;
  authMethod: AuthMethod;
  codeChannel?: CodeChannel;
  status: RecipientStatus;
  addedAt: string;

  // ── set as the signing session progresses; all server-written ──
  notifiedAt?: string;
  openedAt?: string;
  /** Presence of this is what gates access to the document bytes. C-01, C-08. */
  consentedAt?: string;
  consentDisclosureVersion?: string;
  adoptedSignaturePath?: string;
  adoptedInitialsPath?: string;
  adoptionMethod?: 'typed' | 'drawn' | 'uploaded';
  completedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  /** Hash only — the raw token exists solely in the email and the signer's URL. */
  tokenHash?: string;
  tokenExpiresAt?: string;
  codeFailures?: number;
  codeLockedUntil?: string;
  correctionHistory?: { at: string; field: string; oldValue: string; newValue: string }[];
}

/**
 * Treats null as absent.
 *
 * Firebase callables serialise `undefined` to `null` over the wire, so an
 * optional field the client "omitted" by setting it to undefined arrives as
 * null — which `.optional()` rejects, producing an error the user cannot act on
 * ("Expected 'phone' | 'sms' | ..., received null"). Callers should omit the key
 * entirely, but the server must not depend on them getting that right.
 */
const absentIfNull = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === null ? undefined : v), schema.optional());

export const recipientInputSchema = z.object({
  roleId: z.string().min(1, 'Pick a signing role'),
  name: z.string().trim().min(1, 'Name is required').max(120),
  email: z.string().trim().email('Enter a valid email address').max(254),
  phone: absentIfNull(z.string().trim().max(40)),
  company: absentIfNull(z.string().trim().max(120)),
  title: absentIfNull(z.string().trim().max(120)),
  recipientRole: z.enum(RECIPIENT_ROLES),
  routingOrder: z.number().int().min(1).max(50),
  authMethod: z.enum(AUTH_METHODS),
  codeChannel: absentIfNull(z.enum(CODE_CHANNELS)),
});
export type RecipientInput = z.infer<typeof recipientInputSchema>;

/** Recipients who receive a signing link and must act. */
export function isActionable(r: Pick<Recipient, 'recipientRole'>): boolean {
  return r.recipientRole !== 'cc';
}

/** Recipients who apply a signature. */
export function signs(r: Pick<Recipient, 'recipientRole'>): boolean {
  return RECIPIENT_ROLE_INFO[r.recipientRole].signs;
}

/**
 * Groups recipients into routing waves.
 *
 * Equal `routingOrder` values form one wave and are notified together; that
 * single mechanism produces sequential, parallel and mixed routing without
 * three separate code paths. CCs are excluded — they receive the completed
 * package at the end, not a signing link.
 */
export function routingWaves(recipients: Recipient[]): Recipient[][] {
  const actionable = recipients.filter(isActionable);
  const orders = [...new Set(actionable.map((r) => r.routingOrder))].sort((a, b) => a - b);
  return orders.map((o) => actionable.filter((r) => r.routingOrder === o));
}

/** The wave that should be notified next, or null when everyone has finished. */
export function nextWave(recipients: Recipient[]): Recipient[] | null {
  for (const wave of routingWaves(recipients)) {
    if (wave.some((r) => !['completed', 'declined'].includes(r.status))) return wave;
  }
  return null;
}

export interface SendReadiness { ready: boolean; problems: string[] }

/**
 * Everything that must be true before an envelope can be sent.
 *
 * Checked in the UI for feedback and again on the server, which is the control.
 * These are correctness guards, not style: an envelope sent with an unassigned
 * field produces a document nobody can complete.
 */
export function checkSendReadiness(
  recipients: Recipient[],
  roles: { roleId: string; name: string }[],
  fields: { roleId: string; required: boolean }[],
  documentCount: number
): SendReadiness {
  const problems: string[] = [];

  if (documentCount === 0) problems.push('Add at least one document.');
  if (recipients.length === 0) problems.push('Add at least one recipient.');

  const signers = recipients.filter(signs);
  if (recipients.length > 0 && signers.length === 0) {
    problems.push('At least one recipient must be a signer.');
  }

  const seen = new Set<string>();
  for (const r of recipients) {
    const key = r.email.trim().toLowerCase();
    if (seen.has(key)) problems.push(`${r.email} appears more than once.`);
    seen.add(key);
  }

  for (const r of recipients) {
    if (r.authMethod === 'access_code' && !r.codeChannel) {
      problems.push(`Choose how you will give ${r.name} their access code.`);
    }
  }

  const rolesWithPeople = new Set(recipients.filter(signs).map((r) => r.roleId));
  for (const f of fields) {
    if (!rolesWithPeople.has(f.roleId)) {
      const role = roles.find((x) => x.roleId === f.roleId);
      problems.push(`No signer is assigned to the role "${role?.name ?? f.roleId}", which has fields on it.`);
      break;
    }
  }

  for (const r of signers) {
    if (!fields.some((f) => f.roleId === r.roleId && f.required)) {
      problems.push(`${r.name} has no required fields to complete.`);
    }
  }

  return { ready: problems.length === 0, problems: [...new Set(problems)] };
}
